<template>
  <data-loader>
    <calculator-modal
      v-if="showCalculator"
      @close="showCalculator = false"
    ></calculator-modal>
    <page-layout>
      <template v-slot:header>
        <simulation-header
          :title="simulationName"
          :subtitle="userName"
          :time-left-in-seconds="timeLeftInSeconds"
          :question-number="flashcardIndex + 1"
          :total-questions="flashcards.length"
          :include-calculator="true"
          @calculator-clicked="showCalculator = !showCalculator"
        ></simulation-header>
      </template>
      <div
        class="flashcard-content two-column"
        data-test="flashcard-content"
      >
        <div
          class="first-column"
        >
          <scenario-box :flashcard-id="flashcard.id"></scenario-box>
          <asset-tray
            :flashcard="flashcard"
          ></asset-tray>
        </div>
        <flashcard-body
          :flashcard="flashcard"
          :flashcard-state="flashcardState"
          @answers-selected="answersSelected"
          @set-raw-answer-data="setRawAnswerData"
        ></flashcard-body>
      </div>
      <template v-slot:footer>
        <page-footer
          :button-text="nextFlashcardId ? 'Next' : 'Finish'"
          @next-button-clicked="gotoNextFlashcard"
        >
          <template
            v-if="allowBookmarks"
            v-slot:secondary
          >
            <bookmark-flag
              :has-bookmark="hasBookmark"
              @toggle-bookmark="toggleBookmark"
            ></bookmark-flag>
          </template>
        </page-footer>
      </template>
    </page-layout>
  </data-loader>
</template>

<script>
/**
 * This is the flashcard view for simulations with only one section.
 * It was particularly made to work with Nursing simulations and depends on
 * same mixins as the multi-sections simulations like the Dental one.
 */
import AccommodationsMixin from '@/mixins/accommodations';
import DataLoader from '@/components/sims/DataLoader.vue';
import PageLayout from '@components/sims/single-section-sim/PageLayout.vue';
import PageFooter from '@components/sims/single-section-sim/PageFooter.vue';
import BookmarkFlag from '@components/sims/single-section-sim/BookmarkFlag.vue';
import SimulationHeader from '@components/sims/single-section-sim/SimulationHeader.vue';
import FlashcardBody from '@components/sims/single-section-sim/FlashcardBody.vue';
import WuxSimulationsMixin from '@/mixins/wux-simulations';
import BookmarksMixin from '@/mixins/bookmarks';
import CalculatorModal from '@/components/calculator/CalculatorModal.vue';
import { arraysEquivalent } from '@/utils/arrays';
import FlashcardViewMixin from '@/mixins/flashcardViews';
import AssetTray from '@components/sims/single-section-sim/AssetTray.vue';
import ScenarioBox from '@components/sims/single-section-sim/ScenarioBox.vue';
import _ from 'lodash';

// temporary until we have a better way to determine supported flashcard types
const supportedFlashcardTypes = [
  'Multiple Choice',
  'Numeric Entry',
  'Ordered Response',
  'SATA',
  'Drag and Drop',
  'Bowtie',
  'Matrix Grid',
  'Highlight',
  'Cloze / Dropdown',
  'Hotspot',
];
const flashcardTypesWithRawAnswerData = ['Numeric Entry', 'Ordered Response', 'Matrix Grid', 'Hotspot'];

export default {
  name: 'SimulationFlashcardsOneSection',
  components: {
    AssetTray,
    BookmarkFlag,
    DataLoader,
    PageLayout,
    FlashcardBody,
    PageFooter,
    ScenarioBox,
    SimulationHeader,
    CalculatorModal,
  },
  mixins: [AccommodationsMixin, WuxSimulationsMixin, FlashcardViewMixin, BookmarksMixin],
  data() {
    return {
      timeLeftInSeconds: null,
      timerInterval: null,
      showCalculator: false,
      answerIds: [],
      rawAnswerData: {},
    };
  },
  computed: {
    allAnswerIds() {
      return this.flashcard?.answers?.map(a => a.id) || [];
    },
    allowBookmarks() {
      return this.simConfig?.allowBookmarks;
    },
    filtered() {
      return this.$route.query?.filter;
    },
    flashcard() {
      return this.flashcards[this.flashcardIndex] || {};
    },
    flashcardId() {
      return this.$route.params['flashcardId'];
    },
    flashcardIndex() {
      return this.flashcards.findIndex( f => f.id == this.flashcardId );
    },
    flashcardSetIndex() {
      return this.flashcardSetIds.findIndex( id => id == this.flashcardId );
    },
    flashcardSetIds() {
      return this.$store.getters['flashcardSet/ids'];
    },
    flashcardState() {
      return this.getFlashcardState(this.flashcardId, this.simId);
    },
    nextFlashcardId() {
      if (this.filtered) {
        return this.flashcardSetIds[this.flashcardSetIndex + 1];
      }

      if (this.flashcardIndex >= this.flashcards.length - 1) return null;
      return this.flashcards[this.flashcardIndex + 1].id;
    },
    simulationName() {
      return this.simulation?.name;
    },
    troubleshooting() {
      return {
        flashcard: this.flashcard,
      };
    },
    userName() {
      const user = this.$store.getters['user/get'];
      const firstName = user?.firstName || '';
      const lastName = user?.lastName || '';
      return `${firstName} ${lastName}`.trim();
    },
    usesRawAnswerData() {
      return flashcardTypesWithRawAnswerData.includes(this.flashcard.type);
    },
  },
  watch: {
    flashcard(newState, oldState) {
      if (newState.id !== oldState.id) {
        this.resetAnswer();
      }
    },
  },
  mounted() {
    this.timerInterval = setInterval(() => {
      try {
        const timeLeft = this.calculateTimeLeft();
        if (timeLeft < 0) {
          clearInterval(this.timerInterval);
          this.submitSection();
        }
        this.timeLeftInSeconds = timeLeft;
      }
      catch(e) {
        clearInterval(this.timerInterval);
      }
    }, 250);

    this.resetAnswer();
  },
  beforeUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  },
  methods: {
    answersSelected(answerIds) {
      this.answerIds = Array.isArray(answerIds) ? [...answerIds] : [];
      this.updateAnswer();
    },
    calculateTimeLeft() {
      if (!this.categoryStatesFiltered[0]?.mobileCreatedAt) return null;
      if (this.simConfig.timeInMinutes === 0) return null;
      let simTimeInMinutes = this.getTimeByCategory(this.simConfig.categoryId) || this.simConfig.timeInMinutes;
      return this.timeLeft(
        this.categoryStatesFiltered[0].mobileCreatedAt,
        simTimeInMinutes
      ) / 1000;
    },
    gotoNextFlashcard() {
      this.updateAnswer();

      const query = (this.filtered && this.flashcardSetIds.includes(this.nextFlashcardId)) ? { filter: true } : {};

      if (this.nextFlashcardId) {
        this.$router.push({
          name: 'simFlashcard#show',
          params: {
            simId: this.simulation.id,
            flashcardId: this.nextFlashcardId,
          },
          query,
        });
      } else if (this.allowBookmarks) {
        this.$router.push({
          name: 'sims#review',
          params: {
            simId: this.simulation.id,
          },
        });
      } else {
        this.$router.push({
          name: 'sims#outro',
          params: {
            simId: this.simulation.id,
          },
        });
      }
    },
    isAnswerIdValid(answerId) {
      return this.allAnswerIds.includes(answerId);
    },
    resetAnswer() {
      if (this.usesRawAnswerData) {
        this.rawAnswerData = this.flashcardState.rawAnswerData || {};
        this.answerIds = [];
      } else {
        this.rawAnswerData = {};
        this.answerIds = Array.isArray(this.flashcardState?.answerIds)
         ? [...this.flashcardState.answerIds]
         : [];
      }
    },
    setRawAnswerData(rawAnswerData) {
      this.rawAnswerData = rawAnswerData;
      this.updateAnswer();
    },
    updateAnswer() {
      if (!supportedFlashcardTypes.includes(this.flashcard.type)) {
        console.error(`Flashcard type ${this.flashcard.type} is not supported`);
        console.error('Supported flashcard types are: ', supportedFlashcardTypes);
        return;
      }

      if (this.calculateTimeLeft() < 0) return false;

      if (this.usesRawAnswerData) {
        return this.updateAnswerWithRawAnswerData();
      } else {
        return this.updateAnswerWithAnswerIds();
      }
    },
    updateAnswerWithAnswerIds() {
      const hasSavedAnswer = Array.isArray(this.flashcardState?.answerIds);

      if (hasSavedAnswer && arraysEquivalent(this.flashcardState.answerIds, this.answerIds)) {
        return false;
      }

      if (this.answerIds.some(id => !this.isAnswerIdValid(id))) {
        return false;
      }

      const correct = this.evaluateAnswer(this.flashcard, this.answerIds, {});

      const flashcardStatusId = (this.answerIds.length > 0)
        ? this.$store.getters['flashcardStatuses/greenFlashcardStatus'].id
        : this.$store.getters['flashcardStatuses/unansweredFlashcardStatus'].id;

      const params = {
        correct: correct,
        flashcardId: this.flashcard.id,
        flashcardStatusId,
        categoryId: +this.simId,
        answerIds: this.answerIds,
      };

      this.createFlashcardState(params);
      this.$analytics.events.updateSimulationAnswer({
        simulation: this.simulation,
        section: this.section,
        flashcard: this.flashcard,
        answerIds: this.answerIds,
      });
      return true;
    },
    updateAnswerWithRawAnswerData() {
      const hasSavedAnswer = this.flashcardState?.rawAnswerData && !_.isEmpty(this.flashcardState.rawAnswerData);

      if (hasSavedAnswer && _.isEqual(this.flashcardState.rawAnswerData, this.rawAnswerData)) {
        return false;
      }

      const correct = this.evaluateAnswer(this.flashcard, [], this.rawAnswerData || {});

      const flashcardStatusId = (this.rawAnswerData)
        ? this.$store.getters['flashcardStatuses/greenFlashcardStatus'].id
        : this.$store.getters['flashcardStatuses/unansweredFlashcardStatus'].id;

      const params = {
        correct: correct,
        flashcardId: this.flashcard.id,
        flashcardStatusId,
        categoryId: +this.simId,
        rawAnswerData: this.rawAnswerData,
      };

      this.createFlashcardState(params);
      this.$analytics.events.updateSimulationAnswer({
        simulation: this.simulation,
        section: this.section,
        flashcard: this.flashcard,
        answerIds: this.answerIds,
        rawAnswerData: this.rawAnswerData,
      });
      return true;
    },
  },
};
</script>

<style scoped lang="scss">

.flashcard-content {
  display: grid;
  grid-template-columns: 1fr;
  max-width: 50rem;
  min-height: 100%;
  margin: 0 auto;

  &.two-column:has(.scenario-box, .asset-tray) {
    max-width: 100%;
    grid-template-columns: 1fr 1fr;
  }

  // second child has a border on the left
  > :nth-child(1) {
    border-right: 1px solid #d9d9d9;
  }

  > * {
    padding: 1.5rem;
  }

  .first-column {
    display: none;
    min-width: 40%;

    &:has(.scenario-box, .asset-tray) {
      display: block;
    }
  }
}

  .first-column > :first-child {
  margin-top: 0;
}
</style>
